import React, { useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  AppBar,
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Container,
  Divider,
  Grid,
  MenuItem,
  Paper,
  Stack,
  TextField,
  Toolbar,
  Typography,
} from '@mui/material';
import {
  AccountBalance as AccountBalanceIcon,
  Work as WorkIcon,
  Handyman as HandymanIcon,
} from '@mui/icons-material';

const contactDetails = {
  person: 'Steffen Göcking',
  phone: '+49 152 22096381',
  email: 'info@immo-automation.de',
};

const openJobs = [
  {
    title: 'Hausmeister (m/w/d)',
    model: 'Minijob-Basis',
    location: 'Rheine & Osnabrück',
    icon: <WorkIcon sx={{ color: '#0f2a4a' }} />,
    summary: 'Wir sind ein privater Vermieter mit Wohnhäusern in Rheine und Osnabrück. Die Verwaltung machen wir selbst, die Wege sind kurz und Entscheidungen fallen schnell.',
    points: [
      'Objektkontrolle: regelmässige Runde durch Haus und Keller, Treppenhaus, Beleuchtung, Rauchmelder und Zählerstände. Mängel meldest du uns.',
      'Kleine Reparaturen: Türen, Schlösser, Silikonfugen, tropfende Armaturen, Leuchtmittel und Kleinigkeiten am Haus.',
      'Aussenanlagen: Rasen, Hecke, Laub sowie Mülltonnen raus und rein.',
      'Winterdienst: Schneeräumen und Streuen nach Absprache.',
      'Mieterkontakt: Handwerkern Zugang verschaffen, Wohnungsübergaben begleiten, Schlüssel übergeben.',
      'Was wir bieten: 16,50 EUR pro Std., ca. 36 Std./Monat, bis 603 EUR/Monat, Firmenfahrzeug, Werkzeug, Material und Arbeitskleidung werden gestellt.',
    ],
  },
  {
    title: 'Bauhelfer / Renovierungshelfer (m/w/d)',
    model: 'Minijob-Basis',
    location: 'Rheine & Osnabrück',
    icon: <HandymanIcon sx={{ color: '#0f2a4a' }} />,
    summary: 'Wir renovieren Wohnungen selbst statt über Fremdfirmen. Dafür suchen wir eine helfende Hand, die mit anpackt.',
    points: [
      'Renovierung nach Auszug: Wände streichen, Tapeten entfernen, Vinyl oder Laminat verlegen, Sockelleisten und Akrylfugen',
      'Vorbereitung: Entrümpeln, Abkleben, Material transportieren, Baustelle sauber halten und Schutt wegbringen',
      'Zuarbeit bei grösseren Arbeiten: Material anreichen, Demontage, Aufräumen',
      'Kleinere Arbeiten wie Türen einpassen, Regale und Leuchten montieren und kleine Ausbesserungen',
      'Was wir bieten: 15,00 EUR pro Std., Einsätze in Blöcken, Werkzeug und Material werden gestellt',
    ],
  },
  {
    title: 'Aushilfe Buchhaltung (m/w/d)',
    model: 'Teilzeit oder Minijob',
    location: 'Rheine / Hybrid',
    icon: <AccountBalanceIcon sx={{ color: '#0f2a4a' }} />,
    summary: 'Wir suchen eine verlässliche Unterstützung für die vorbereitende Buchhaltung und die saubere Organisation im Hintergrund.',
    points: [
      'Belege sichten, sortieren und für die Buchhaltung vorbereiten',
      'Unterstützung bei OP-Listen, Kontenabstimmungen und Rückfragen',
      'Rechnungen, Ablagen und Belegdokumentation strukturiert pflegen',
      'Sauber, diskret und zuverlässig im Tagesgeschäft mitarbeiten',
      'Was wir bieten: 15,00 EUR pro Std., Einsätze in Blöcken, Werkzeug und Material werden gestellt',
    ],
  },
];

const KarrierePage = () => {
  const navigate = useNavigate();
  const formRef = useRef(null);
  const [submitted, setSubmitted] = useState(false);
  const [formData, setFormData] = useState({
    vorname: '',
    nachname: '',
    email: '',
    telefon: '',
    position: '',
    verfuegbar_ab: '',
    nachricht: '',
    cvName: '',
  });

  const applyForJob = (jobTitle) => {
    setFormData((prev) => ({ ...prev, position: jobTitle }));
    if (formRef.current) {
      formRef.current.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  };

  const handleChange = (event) => {
    const { name, value } = event.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleCv = (event) => {
    const file = event.target.files?.[0] || null;
    setFormData((prev) => ({ ...prev, cvName: file ? file.name : '' }));
  };

  const handleSubmit = (event) => {
    event.preventDefault();
    setSubmitted(true);
    setTimeout(() => setSubmitted(false), 6000);
    setFormData((prev) => ({
      ...prev,
      vorname: '',
      nachname: '',
      email: '',
      telefon: '',
      verfuegbar_ab: '',
      nachricht: '',
      cvName: '',
    }));
  };

  return (
    <Box sx={{ bgcolor: '#f6f7f9', minHeight: '100vh' }}>
      <AppBar position="sticky" elevation={0} sx={{ bgcolor: '#1b2c44', borderBottom: '1px solid #2d4262' }}>
        <Toolbar sx={{ py: 1 }}>
          <Typography
            variant="h6"
            sx={{
              flexGrow: 1,
              fontWeight: 800,
              display: 'flex',
              alignItems: 'center',
              gap: 1,
              cursor: 'pointer',
            }}
            onClick={() => navigate('/')}
          >
            <AccountBalanceIcon sx={{ color: '#7ed2c6' }} /> immo-automation
          </Typography>
          <Button sx={{ color: 'white', textTransform: 'none' }} onClick={() => navigate('/leistungen/sondereigentumsverwaltung')}>
            SEV-Leistung
          </Button>
          <Button sx={{ color: 'white', textTransform: 'none' }} onClick={() => navigate('/immobilien')}>
            Immobilien
          </Button>
          <Button sx={{ color: '#e8efff', textTransform: 'none', fontWeight: 700 }} onClick={() => navigate('/karriere')}>
            Karriere
          </Button>
          <Button sx={{ color: 'white', textTransform: 'none' }} onClick={() => navigate('/kontakt')}>
            Kontakt
          </Button>
          <Button
            variant="outlined"
            sx={{ ml: 1, borderColor: '#7ed2c6', color: '#e8efff', textTransform: 'none', fontWeight: 700 }}
            onClick={() => navigate('/login')}
          >
            Login
          </Button>
        </Toolbar>
      </AppBar>

      <Box
        sx={{
          background: 'linear-gradient(180deg, #ffffff 0%, #f4f7fb 100%)',
          borderBottom: '1px solid #d7dde6',
          pt: { xs: 7, md: 9 },
          pb: { xs: 7, md: 8 },
        }}
      >
        <Container maxWidth="lg">
          <Stack spacing={2.2} sx={{ maxWidth: 900 }}>
            <Typography variant="overline" sx={{ letterSpacing: 1.4, color: '#334e6f', fontWeight: 700 }}>
              KARRIERE BEI IMMO-AUTOMATION
            </Typography>
            <Typography
              variant="h2"
              sx={{
                fontWeight: 800,
                lineHeight: 1.15,
                color: '#16283f',
                fontFamily: '"Merriweather", "Georgia", serif',
                fontSize: { xs: '2rem', md: '3rem' },
              }}
            >
              Verlässliche Menschen für verlässliche Arbeit.
            </Typography>
            <Typography variant="h6" sx={{ color: '#334e6f', lineHeight: 1.55, maxWidth: 780, fontWeight: 500 }}>
              Wir suchen Kolleginnen und Kollegen mit Bodenhaftung, Sorgfalt und Einsatzbereitschaft.
            </Typography>
            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.3} sx={{ pt: 1 }}>
              <Button
                variant="contained"
                sx={{ bgcolor: '#1b2c44', textTransform: 'none', fontWeight: 700, px: 3, borderRadius: 1.5 }}
                onClick={() => applyForJob('Aushilfe Buchhaltung')}
              >
                Zu den Stellen
              </Button>
              <Button
                variant="outlined"
                sx={{ borderColor: '#1b2c44', color: '#1b2c44', textTransform: 'none', fontWeight: 700, borderRadius: 1.5 }}
                onClick={() => navigate('/kontakt')}
              >
                Allgemeine Anfrage
              </Button>
            </Stack>
          </Stack>
        </Container>
      </Box>

      <Container maxWidth="lg" sx={{ py: 7 }}>
        <Stack spacing={1.2} sx={{ mb: 3 }}>
          <Typography variant="overline" sx={{ color: '#334e6f', letterSpacing: 1.2, fontWeight: 700 }}>
            OFFENE STELLEN
          </Typography>
          <Typography variant="h4" sx={{ fontWeight: 800, color: '#16283f', fontFamily: '"Merriweather", "Georgia", serif' }}>
            Aktuell suchen wir für diese Positionen
          </Typography>
        </Stack>

        <Grid container spacing={2}>
          {openJobs.map((item) => (
            <Grid item xs={12} md={4} key={item.title}>
              <Card sx={{ borderRadius: 2, border: '1px solid #cad3df', height: '100%', boxShadow: 'none' }}>
                <CardContent>
                  <Stack spacing={1.3}>
                    {item.icon}
                    <Typography variant="h6" sx={{ fontWeight: 800, color: '#16283f' }}>
                      {item.title}
                    </Typography>
                    <Typography variant="body2" sx={{ color: '#3f556f', fontWeight: 600 }}>
                      {item.model}
                    </Typography>
                    <Typography variant="body2" sx={{ color: '#60758f' }}>
                      Standort: {item.location}
                    </Typography>
                    {item.summary ? (
                      <Typography variant="body2" sx={{ color: '#41566f', lineHeight: 1.5 }}>
                        {item.summary}
                      </Typography>
                    ) : null}
                    <Divider sx={{ borderColor: '#dce3ee' }} />
                    {item.points.map((line) => (
                      <Typography key={line} variant="body2" sx={{ color: '#41566f', lineHeight: 1.5 }}>
                        - {line}
                      </Typography>
                    ))}
                    <Box sx={{ pt: 1 }}>
                      <Button
                        variant="outlined"
                        sx={{ textTransform: 'none', borderColor: '#1b2c44', color: '#1b2c44', fontWeight: 700 }}
                        onClick={() => applyForJob(item.title)}
                      >
                        Jetzt bewerben
                      </Button>
                    </Box>
                  </Stack>
                </CardContent>
              </Card>
            </Grid>
          ))}
        </Grid>
      </Container>

      <Container ref={formRef} maxWidth="lg" sx={{ pb: 10 }}>
        <Paper elevation={0} sx={{ borderRadius: 2, border: '1px solid #cad3df', p: { xs: 2.5, md: 4 }, bgcolor: '#ffffff' }}>
          <Stack spacing={1.2} sx={{ mb: 3 }}>
            <Typography variant="overline" sx={{ color: '#334e6f', letterSpacing: 1.2, fontWeight: 700 }}>
              BEWERBUNG
            </Typography>
            <Typography variant="h4" sx={{ fontWeight: 800, color: '#16283f', fontFamily: '"Merriweather", "Georgia", serif' }}>
              Bewerbung und Kontakt
            </Typography>
            <Typography variant="body1" sx={{ color: '#4a5f78' }}>
              Senden Sie uns Ihre Eckdaten. Wir melden uns zeitnah für ein erstes Gespräch.
            </Typography>
          </Stack>

          {submitted ? (
            <Alert severity="success" sx={{ mb: 3 }}>
              Vielen Dank. Ihre Bewerbung wurde vorgemerkt. Wir melden uns kurzfristig bei Ihnen.
            </Alert>
          ) : null}

          <Box component="form" onSubmit={handleSubmit}>
            <Grid container spacing={2}>
              <Grid item xs={12} md={6}>
                <TextField fullWidth required name="vorname" label="Vorname" value={formData.vorname} onChange={handleChange} />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField fullWidth required name="nachname" label="Nachname" value={formData.nachname} onChange={handleChange} />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField fullWidth required type="email" name="email" label="E-Mail" value={formData.email} onChange={handleChange} />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField fullWidth name="telefon" label="Telefon" value={formData.telefon} onChange={handleChange} />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField select fullWidth required name="position" label="Gewünschte Position" value={formData.position} onChange={handleChange}>
                  <MenuItem value="Hausmeister (m/w/d)">Hausmeister (m/w/d)</MenuItem>
                  <MenuItem value="Aushilfe Buchhaltung">Aushilfe Buchhaltung</MenuItem>
                  <MenuItem value="Monatehelfer für Bautätigkeiten">Monatehelfer für Bautätigkeiten</MenuItem>
                  <MenuItem value="Initiativbewerbung">Initiativbewerbung</MenuItem>
                </TextField>
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField fullWidth type="date" name="verfügbar_ab" label="Verfügbar ab" value={formData.verfuegbar_ab} onChange={handleChange} InputLabelProps={{ shrink: true }} />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  multiline
                  minRows={5}
                  name="nachricht"
                  label="Kurzprofil / Nachricht"
                  value={formData.nachricht}
                  onChange={handleChange}
                  placeholder="Erzählen Sie uns kurz von Ihrer Erfahrung und Motivation."
                />
              </Grid>
              <Grid item xs={12}>
                <Button variant="outlined" component="label" sx={{ textTransform: 'none', borderColor: '#1b2c44', color: '#1b2c44' }}>
                  Lebenslauf hochladen (PDF)
                  <input type="file" hidden accept=".pdf" onChange={handleCv} />
                </Button>
                <Typography variant="body2" sx={{ color: '#60758f', mt: 1 }}>
                  {formData.cvName ? `Datei: ${formData.cvName}` : 'Noch keine Datei ausgewählt'}
                </Typography>
              </Grid>
              <Grid item xs={12}>
                <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5}>
                  <Button type="submit" variant="contained" sx={{ bgcolor: '#1b2c44', textTransform: 'none', fontWeight: 700, px: 3 }}>
                    Bewerbung absenden
                  </Button>
                  <Button type="button" variant="text" sx={{ textTransform: 'none', color: '#1b2c44' }} onClick={() => navigate('/kontakt')}>
                    Lieber über Kontaktseite schreiben
                  </Button>
                </Stack>
              </Grid>
              <Grid item xs={12}>
                <Paper variant="outlined" sx={{ p: 2, borderRadius: 1.5, borderColor: '#d7dde6', bgcolor: '#f8fafc' }}>
                  <Typography variant="subtitle2" sx={{ fontWeight: 800, color: '#16283f', mb: 0.5 }}>
                    Ansprechpartner
                  </Typography>
                  <Typography variant="body2" sx={{ color: '#4a5f78', lineHeight: 1.6 }}>
                    {contactDetails.person} · Tel./WhatsApp: {contactDetails.phone} · E-Mail: {contactDetails.email}
                  </Typography>
                </Paper>
              </Grid>
            </Grid>
          </Box>
        </Paper>
      </Container>
    </Box>
  );
};

export default KarrierePage;
